// Apparent visual magnitudes of the planets seen from anywhere, not only
// from the Earth: Mallama and Hilton (2018), "Computing apparent planetary
// magnitudes for The Astronomical Almanac", Astronomy and Computing 25,
// 10-24, doi:10.1016/j.ascom.2018.08.002 (arXiv:1808.01973). The geocentric
// equations and the supplementary ones the paper gives for the phase angles
// a spacecraft sees:
//
//   Mercury  Eq. 2, 2 < a < 170
//   Venus    Eq. 3 to a = 163.7, Eq. 4 to 179
//   Earth    Eq. 5 (V1(0) = -3.99, the "realistic clouds" phase curve)
//   Mars     Eq. 6 to a = 50, Eq. 7 beyond (its rotation and season terms
//            L(lambda_e) and L(Ls) left out: a few hundredths of a magnitude)
//   Jupiter  Eq. 8 to a = 12, Eq. 9 to 130
//   Saturn   Eq. 10 with its rings to a = 6.5, then the globe's phase curve
//            (Eq. 12, to 150) from there: the paper has no ring model past 6.5
//   Uranus   Eq. 15 to a = 154 (the sub-latitude term left out)
//   Neptune  Eq. 17 to a = 133
//
// Past each equation's range the phase curve continues as a Lambert
// sphere's from the last fitted angle, so the brightness keeps falling
// smoothly toward the thin crescent. r and d are the distances from the Sun
// and from the observer in AU, a the phase angle in degrees, ringTilt the
// rings' opening to the observer in degrees. scripts/site/parades.js keeps
// its own geocentric copy for the site's pages.

const DEG = Math.PI / 180;

// The Lambert sphere's phase integral, relative to full phase.
function lambert(aDeg) {
  const a = Math.min(Math.PI, Math.max(0, aDeg * DEG));
  return Math.max(1e-6, (Math.sin(a) + (Math.PI - a) * Math.cos(a)) / Math.PI);
}

// Magnitude at 1 AU from both (the 5 log10(r d) term added by the caller).
function reduced(body, a, ringTilt) {
  switch (body) {
    case 'mercury':
      return -0.613 + 6.328e-2 * a - 1.6336e-3 * a ** 2 + 3.3644e-5 * a ** 3 - 3.4265e-7 * a ** 4 + 1.6893e-9 * a ** 5 - 3.0334e-12 * a ** 6;
    case 'venus':
      return a <= 163.7
        ? -4.384 - 1.044e-3 * a + 3.687e-4 * a ** 2 - 2.814e-6 * a ** 3 + 8.938e-9 * a ** 4
        : 236.05828 - 2.81914 * a + 8.39034e-3 * a ** 2;
    case 'earth':
      return -3.99 - 1.060e-3 * a + 2.054e-4 * a ** 2;
    case 'mars':
      return a <= 50
        ? -1.601 + 0.02267 * a - 0.0001302 * a ** 2
        : -0.367 - 0.02573 * a + 0.0003445 * a ** 2;
    case 'jupiter': {
      if (a <= 12) return -9.395 - 3.7e-4 * a + 6.16e-4 * a ** 2;
      const x = a / 180;
      return -9.428 - 2.5 * Math.log10(1.0 - 1.507 * x - 0.363 * x ** 2 - 0.062 * x ** 3 + 2.809 * x ** 4 - 1.876 * x ** 5);
    }
    case 'saturn': {
      const sb = Math.sin(Math.abs(ringTilt) * DEG);
      const withRings = (x) => -8.914 - 1.825 * sb + 0.026 * x - 0.378 * sb * Math.exp(-2.25 * x);
      if (a <= 6.5) return withRings(a);
      // Past 6.5 the globe's phase curve (Eq. 12) from where the rings'
      // equation leaves off, the rings taken to fade with the globe: the
      // paper has no ring model there, and the globe alone would drop half
      // a magnitude at once.
      const globe = (x) => -8.94 + 2.446e-4 * x + 2.672e-4 * x ** 2 - 1.505e-6 * x ** 3 + 4.767e-9 * x ** 4;
      return withRings(6.5) + globe(a) - globe(6.5);
    }
    case 'uranus':
      return -7.110 + 6.587e-3 * a + 1.045e-4 * a ** 2;
    case 'neptune':
      return -7.00 + 7.944e-3 * a + 9.617e-5 * a ** 2;
    default:
      return NaN;
  }
}

const LIMIT = { mercury: 170, venus: 179, earth: 180, mars: 180, jupiter: 130, saturn: 150, uranus: 154, neptune: 133 };

export function planetMagnitude(body, r, d, a, ringTilt = 0) {
  const limit = LIMIT[body];
  if (limit == null) return NaN;
  const base = 5 * Math.log10(Math.max(1e-9, r * d));
  if (a <= limit) return base + reduced(body, a, ringTilt);
  return base + reduced(body, limit, ringTilt) - 2.5 * Math.log10(lambert(a) / lambert(limit));
}
